// AuthScreen.jsx V2 — LiftLithe authentication screen (extracted from WorkoutApp.jsx)
import { useState, useEffect } from "react";
import { Plus, Lightning, EnvelopeSimple, Lock, Eye, EyeSlash, User, Cloud, WarningCircle } from "@phosphor-icons/react";
import { supabase } from "../../supabaseClient";
import { T, getHeaderBg } from "../../themes";
import Btn from "../Btn";
import Field from "../Field";
import ComicCard from "../ComicCard";
import Spinner from "../Spinner";

export default function AuthScreen({ persona }){
  const [tab,setTab]=useState("login");
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [name,setName]=useState("");
  const [showPass,setShowPass]=useState(false);
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [resetOk,setResetOk]=useState("");
  const [ageConfirmed,setAgeConfirmed]=useState(false);
  const [gdprConsent,setGdprConsent]=useState(false);
  const [resendCountdown,setResendCountdown]=useState(0);
  const [resendOk,setResendOk]=useState("");
  useEffect(()=>{
    if(resendCountdown<=0) return;
    const t=setTimeout(()=>setResendCountdown(c=>c-1),1000);
    return ()=>clearTimeout(t);
  },[resendCountdown]);
  async function handleLogin(){
    if(!email||!email.trim()){setErr("Please enter your email address");return;}
    if(!pass){setErr("Please enter your password");return;}
    setLoading(true); setErr("");
    const {error}=await supabase.auth.signInWithPassword({email,password:pass});
    if(error) setErr(error.message);
    setLoading(false);
  }
  async function handleSignup(){
    if(!email||!pass||!name){setErr("All fields required.");return;}
    if(pass.length<8){setErr("Password must be 8+ characters.");return;}
    setLoading(true); setErr("");
    const {data,error}=await supabase.auth.signUp({email,password:pass,options:{data:{display_name:name}}});
    if(error) setErr(error.message);
    else if(data?.user?.identities?.length===0) setErr("Email already registered. Try logging in.");
    else { setErr("Check your email to confirm, then log in."); setResendCountdown(60); }
    setLoading(false);
  }
  async function handleResend(){
    setResendOk("");
    try{ await supabase.auth.resend({type:"signup",email}); }catch(_){}
    setResendOk("Email sent!");
    setTimeout(()=>setResendOk(""),2000);
    setResendCountdown(60);
  }
  async function handleReset(e){
    if(e&&e.preventDefault) e.preventDefault();
    if(!email){setErr("Enter your email address.");return;}
    setLoading(true); setErr("");
    const {error}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:"https://www.lithe.fit/workout"});
    if(error) setErr(error.message);
    else setResetOk("Reset link sent! Check your inbox.");
    setLoading(false);
  }
  function openLink(url){ try{ window.open(url,"_blank"); }catch(_){} }
  return(
    <div style={{background:T.bg,minHeight:"100vh",width:"100%",display:"flex",flexDirection:"column"}}>
      <div style={{background:getHeaderBg(persona||"balanced"),padding:"16px 16px 12px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <img src="/logo192.png" alt="LF" width={38} height={38}
            style={{borderRadius:11,objectFit:"cover",flexShrink:0}}/>
          <div>
            <h1 style={{color:"#fff",fontSize:30,fontWeight:800,margin:0,fontFamily:T.fontDisplay,letterSpacing:"-0.5px",lineHeight:1}}>Lithe.Fit</h1>
            <p style={{color:"rgba(255,255,255,0.7)",fontSize:11,margin:"2px 0 0",fontFamily:T.fontDisplay,fontWeight:500}}>
              Lithe.Fit · {tab==="login"?"Sign in":tab==="reset"?"Reset password":"Create account"}
            </p>
          </div>
        </div>
      </div>
      <div style={{padding:"24px 16px 48px",flex:1,overflowY:"auto",background:T.bg,display:"flex",flexDirection:"column",justifyContent:"center"}}>
        <div style={{display:"flex",border:T.border,boxShadow:T.shadowSm,marginBottom:20,borderRadius:T.radiusSm,overflow:"hidden"}}>
          {[{id:"login",label:"Sign In"},{id:"signup",label:"Create Account"}].map(({id,label},i)=>(
            <button key={id} onClick={()=>{setTab(id);setErr("");setResetOk("");setGdprConsent(false);setAgeConfirmed(false);}} style={{
              flex:1,padding:"12px 0",
              background:tab===id?T.primary:T.surface,
              border:"none",borderRight:i===0?T.border:"none",
              color:tab===id?"#fff":T.textDim,
              fontSize:12,fontWeight:700,fontFamily:T.fontSans,
              cursor:"pointer",
            }}>{label}</button>
          ))}
        </div>
        <ComicCard>
          {tab==="signup"&&<Field label="Display Name" value={name} onChange={e=>setName(e.target.value)} placeholder="Alex, Jordan, Dr. Smith…" icon={<User size={14}/>}/>}
          <Field label="Email Address" value={email} onChange={e=>setEmail(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&(tab==="login"?handleLogin():tab==="reset"?handleReset(e):handleSignup())}
            placeholder="you@example.com" type="email" icon={<EnvelopeSimple size={14}/>}/>
          {tab!=="reset"&&(
            <Field label="Password" value={pass} onChange={e=>setPass(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&(tab==="login"?handleLogin():handleSignup())}
              placeholder={tab==="signup"?"8+ characters…":"••••••••"}
              type={showPass?"text":"password"} icon={<Lock size={14}/>}
              append={<span onClick={()=>setShowPass(v=>!v)} style={{cursor:"pointer"}}>{showPass?<EyeSlash size={14}/>:<Eye size={14}/>}</span>}/>
          )}
          {tab==="login"&&(
            <button onClick={()=>{setTab("reset");setErr("");setResetOk("");}}
              style={{background:"none",border:"none",cursor:"pointer",color:T.textDim,
                fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",padding:"2px 0 10px",
                textDecoration:"underline",display:"block"}}>
              Forgot password?
            </button>
          )}
          {tab==="reset"&&(
            <div style={{marginBottom:10}}>
              <p style={{margin:"0 0 8px",fontSize:11,fontFamily:T.fontSans,color:T.textSub,fontWeight:"bold",lineHeight:1.5}}>
                Enter your email and we'll send a reset link.
              </p>
              {resetOk&&(
                <div style={{background:T.surface,border:`2px solid ${T.primary}`,padding:"9px 12px",color:T.text,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold",marginBottom:10,borderRadius:T.radiusSm}}>
                  {resetOk}
                </div>
              )}
            </div>
          )}
          {err&&(
            <div style={{background:err.startsWith("Check")?T.surface:`${T.red}14`,border:err.startsWith("Check")?`2px solid ${T.primary}`:`1px solid ${T.red}33`,padding:"10px 12px",color:err.startsWith("Check")?T.text:T.red,fontSize:12,fontFamily:T.fontSans,fontWeight:"bold",marginBottom:10,display:"flex",gap:8,alignItems:"flex-start",borderRadius:T.radiusSm}}>
              <WarningCircle size={14} style={{flexShrink:0,marginTop:1}}/><span>{err}</span>
            </div>
          )}
          {err.startsWith("Check")&&(
            <div style={{textAlign:"center",marginBottom:10}}>
              {resendOk?(
                <p style={{margin:0,fontSize:13,fontFamily:T.fontSans,fontWeight:600,color:T.primary}}>{resendOk}</p>
              ):resendCountdown>0?(
                <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,color:T.textDim}}>Resend available in {resendCountdown}s…</p>
              ):(
                <button onClick={handleResend} style={{background:"none",border:"none",cursor:"pointer",color:T.primary,fontSize:13,fontFamily:T.fontSans,fontWeight:600,padding:"4px 0",textDecoration:"underline"}}>
                  Resend confirmation email
                </button>
              )}
            </div>
          )}
          {tab==="reset"?(
            <Btn onClick={handleReset} disabled={loading||!!resetOk} color={T.primary}>
              {loading?<><Spinner/> Sending…</>:<><EnvelopeSimple size={15}/> Send Reset Link</>}
            </Btn>
          ):(
            <>
            {tab==="signup"&&(
              <>
              <div style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:10}}>
                <input type="checkbox" id="lf-age-confirm" checked={ageConfirmed}
                  onChange={e=>setAgeConfirmed(e.target.checked)}
                  style={{marginTop:2,accentColor:T.primary,width:16,height:16,flexShrink:0}}/>
                <label htmlFor="lf-age-confirm" style={{fontSize:11,fontFamily:T.fontSans,color:T.textDim,lineHeight:1.5,cursor:"pointer"}}>I confirm I am 16 years of age or older</label>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:10}}>
                <input type="checkbox" id="lf-gdpr-consent" checked={gdprConsent}
                  onChange={e=>setGdprConsent(e.target.checked)}
                  style={{marginTop:2,accentColor:T.primary,width:16,height:16,flexShrink:0}}/>
                <label htmlFor="lf-gdpr-consent" style={{fontSize:11,fontFamily:T.fontSans,color:T.textDim,lineHeight:1.5,cursor:"pointer"}}>
                  I agree to the{" "}
                  <span onClick={(e)=>{e.preventDefault();e.stopPropagation();openLink("https://www.lithe.fit/privacy");}} style={{color:T.primary,textDecoration:"underline",cursor:"pointer"}}>Privacy Policy</span>
                  {" "}and{" "}
                  <span onClick={(e)=>{e.preventDefault();e.stopPropagation();openLink("https://www.lithe.fit/terms");}} style={{color:T.primary,textDecoration:"underline",cursor:"pointer"}}>Terms of Service</span>
                  . I understand this app does not provide medical advice.
                </label>
              </div>
              </>
            )}
            <Btn onClick={tab==="login"?handleLogin:handleSignup} disabled={loading||(tab==="signup"&&(!gdprConsent||!ageConfirmed))} color={T.primary}>
              {loading?<><Spinner/> {tab==="login"?"Signing In…":"Creating Account…"}</>
                :tab==="login"?<><Lightning size={15}/> Sign In</>:<><Plus size={15}/> Create Account</>}
            </Btn>
            </>
          )}
          {tab==="reset"&&(
            <button onClick={()=>{setTab("login");setErr("");setResetOk("");}}
              style={{background:"none",border:"none",cursor:"pointer",color:T.textDim,
                fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",padding:"8px 0 0",
                textDecoration:"underline",display:"block",textAlign:"center",width:"100%"}}>
              Back to Sign In
            </button>
          )}
        </ComicCard>
        <div style={{display:"flex",gap:6,alignItems:"center",justifyContent:"center",marginTop:20}}>
          <Cloud size={13} color={T.primary} style={{flexShrink:0}}/>
          <p style={{color:T.textDim,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold",margin:0,lineHeight:1.5}}>
            Same account as <strong>NutriLithe</strong>. One login covers both apps.
          </p>
        </div>
      </div>
    </div>
  );
}
